/**
 * This Hook is for getting FeedInstance from feed apis.
 * It returns feedList, a fetch function to invoke,
 * loading state and error state.
 *
 * @example
 * ```
 * const [feeds, fetchFeeds, isLoading, isError] = useFeedApi(32, 10);
 * ```
 *
 * @summary Feed API Hook
 * @author MonkeyFigaro <monkeychen@sohu-inc.com>
 *
 * Created at     : 2019-03-19 18:19:15
 * Last modified  : 2019-08-14 22:52:19
 */

import { jsonGet } from '../../utils/http';
import { useEffect, useState } from 'react';
import { FeedInstance, FeedAPI } from '../../models/feed';
import { useEffectAsync } from '../useEffectAsync';
import { sleep } from '../../utils';

/**
 * @param  {number|string} api - Pass an regionID
 * @param  {number} slice - The max length of the feed list
 * @returns [FeedInstances, doFetch, loading, error]
 */
export const useFeedApi = (regionId: number | string, slice: number): [FeedInstance[], () => void, boolean, boolean] => {
    const [feeds, setFeeds]: [FeedInstance[], any] = useState<FeedInstance[]>([]);
    const [returnFeeds, setReturnFeeds]: [FeedInstance[], any] = useState<FeedInstance[]>([]);
    const [loading, setLoading]: [boolean, any] = useState(true);
    const [error, setError]: [boolean, any] = useState(false);
    /**
     * request state
     * @default 1
     * `0` - stands for `Request end`
     * `1` -  stands for `Will make the first request`
     * `2` - stands for `make request manually`
     */
    const [request, setRequest]: [0 | 1 | 2, any] = useState<0 | 1 | 2>(1);

    useEffectAsync(async () => {
        await sleep(1);
        const url = `//v2.sohu.com/integration-api/mix/region/${regionId}`;
        if (request === 0) {
            // Stop request again when switch
            return;
        }
        if (error) {
            setError(false);
        }
        if (!loading) {
            setLoading(true);
        }
        // TODO: use a `promise.all` way to make delay, this is acceptable temporarily.
        if (request === 2) {
            await sleep(1000);
        }
        const res: FeedAPI = await jsonGet(url) as FeedAPI;
        if (res.status === 200 && res.body.data && res.body.data.length) {
            setLoading(false);
            setFeeds(res.body.data.filter(e => !e.backup && e.url));
        } else {
            setError(true);
            setLoading(false);
        }
        if (request) {
            setRequest(0);
        }
    }, [regionId, slice, request]);

    useEffect(() => {
        setReturnFeeds([...feeds.slice(0, slice)]);
    }, [slice, feeds]);

    const doFetch = () => {
        setRequest(2);
    };

    return [returnFeeds, doFetch, loading, error];
};



// WEBPACK FOOTER //
// ./src/hooks/APIs/useFeedApi.ts